import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import TextGenerationTab from '../components/TextGenerationTab';
import ImageGenerationTab from '../components/ImageGenerationTab';
import VideoGenerationTab from '../components/VideoGenerationTab';
import { ContentType, ClassId, AspectRatio } from '../types';

interface SessionToolsPageProps {}

interface SessionData {
  sessionId: string;
  userId: number;
  apiKeyIndex: number;
  sessionName?: string;
  canConnect: boolean;
  currentConnections: number;
  maxConnections: number;
  expiresAt: string;
}

const SessionToolsPage: React.FC<SessionToolsPageProps> = () => {
  const { sessionId, userId } = useParams<{ sessionId: string; userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [activeTab, setActiveTab] = useState<'text' | 'image' | 'video'>(() => {
    const savedTab = localStorage.getItem('session_active_tab');
    return (savedTab as 'text' | 'image' | 'video') || 'text';
  });

  useEffect(() => {
    if (!sessionId || !userId) {
      setError('세션 ID 또는 사용자 번호가 없습니다.');
      setLoading(false);
      return;
    }

    verifySession();
  }, [sessionId, userId]);

  const verifySession = async () => {
    try {
      setLoading(true);
      setError(null);

      // 세션 유효성 확인
      const verifyResponse = await api.get(`/sessions/${sessionId}/verify`);
      
      if (!verifyResponse.data.can_connect && verifyResponse.data.current_connections >= verifyResponse.data.max_connections) {
        setError('세션 접속자 수가 초과되었습니다.');
        setLoading(false);
        return;
      }

      // 세션 데이터 설정
      const sessionData: SessionData = {
        sessionId: sessionId!,
        userId: parseInt(userId!),
        apiKeyIndex: (parseInt(userId!) - 1) % 20, // Round-Robin 방식
        sessionName: verifyResponse.data.session_name,
        canConnect: verifyResponse.data.can_connect,
        currentConnections: verifyResponse.data.current_connections,
        maxConnections: verifyResponse.data.max_connections,
        expiresAt: verifyResponse.data.expires_at,
      };

      setSessionData(sessionData);
      setLoading(false);

    } catch (err: any) {
      console.error('세션 검증 오류:', err);
      
      if (err.response?.status === 404) {
        setError('세션을 찾을 수 없습니다.');
      } else if (err.response?.status === 410) {
        setError('세션이 만료되었거나 비활성화되었습니다.');
      } else {
        setError(err.response?.data?.detail || '세션 검증에 실패했습니다.');
      }
      
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      if (sessionData) {
        await api.post(`/sessions/${sessionData.sessionId}/disconnect/${sessionData.userId}`);
      }
    } catch (err) {
      console.error('연결 해제 오류:', err);
    } finally {
      navigate('/', { replace: true });
    }
  };

  const handleTabChange = (tab: 'text' | 'image' | 'video') => {
    setActiveTab(tab);
    // 로컬 스토리지에 탭 상태 저장
    localStorage.setItem('session_active_tab', tab);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={24}
          sx={{
            padding: 4,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            textAlign: 'center',
          }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: 'white',
              mb: 3,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
            }}
          >
            세션 정보 로드 중...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: 4,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              textAlign: 'center',
            }}
          >
            <Alert
              severity="error"
              sx={{
                mb: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '& .MuiAlert-message': {
                  color: 'white',
                },
                '& .MuiAlert-icon': {
                  color: '#ff6b6b',
                },
              }}
            >
              {error}
            </Alert>
            
            <Button
              variant="contained"
              onClick={() => navigate('/', { replace: true })}
              sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              홈으로 돌아가기
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!sessionData) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
      }}
    >
      <Navbar />
      
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        {/* 탭 메뉴 */}
        <Paper
          elevation={12}
          sx={{
            borderRadius: 3,
            mb: 1,
            background: 'transparent',
            backdropFilter: 'none',
            border: 'none',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* 슬라이딩 인디케이터 */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: activeTab === 'text' ? '0%' : activeTab === 'image' ? '33.33%' : '66.66%',
              width: '33.33%',
              height: '3px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 2,
            }}
          />
          
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <Button
              onClick={() => handleTabChange('text')}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 0,
                background: 'transparent',
                color: activeTab === 'text' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontWeight: activeTab === 'text' ? 'bold' : 'normal',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'transparent',
                  color: 'white',
                },
              }}
            >
              텍스트 생성
            </Button>
            <Button
              onClick={() => handleTabChange('image')}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 0,
                background: 'transparent',
                color: activeTab === 'image' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontWeight: activeTab === 'image' ? 'bold' : 'normal',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'transparent',
                  color: 'white',
                },
              }}
            >
              이미지 생성
            </Button>
            <Button
              onClick={() => handleTabChange('video')}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 0,
                background: 'transparent',
                color: activeTab === 'video' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontWeight: activeTab === 'video' ? 'bold' : 'normal',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'transparent',
                  color: 'white',
                },
              }}
            >
              영상 생성
            </Button>
          </Box>
        </Paper>

        {/* 탭 컨텐츠 */}
        <Box sx={{ width: '100%' }}>
          {activeTab === 'text' && (
            <TextGenerationTab 
              contentType={'Normal' as ContentType}
              classId={sessionData.sessionId as ClassId}
              sessionId={sessionData.sessionId}
              userId={sessionData.userId}
            />
          )}
          {activeTab === 'image' && (
            <ImageGenerationTab 
              contentType={'Normal' as ContentType}
              classId={sessionData.sessionId as ClassId}
              sessionId={sessionData.sessionId}
              userId={sessionData.userId}
            />
          )}
          {activeTab === 'video' && (
            <VideoGenerationTab 
              contentType={'Normal' as ContentType}
              classId={sessionData.sessionId as ClassId}
              sessionId={sessionData.sessionId}
              userId={sessionData.userId}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default SessionToolsPage; 